/**
 * Check if user has access to protected content
 * This function can be called in useAsyncData before page renders
 * to prevent flash of restricted content. Throws an error if access is denied.
 *
 * @param {Object} entry - The entry data with contentProtection field
 * @param {Object} nuxtApp - The Nuxt app instance from useAsyncData context
 * @param {Object} auth - Authentication data with loggedIn property
 * @param {Object} loyalty - Loyalty data with isLoyaltyUser property
 * @param {Object} userData - User data with userDetails property
 * @throws {Error} - Throws a Nuxt error with redirect URL if access is denied
 */
export const checkContentProtection = (entry, nuxtApp, auth, loyalty, userData) => {
  const contentProtection = entry?.contentProtection;

  if (!contentProtection) {
    return;
  }

  const { $localeRootPath } = nuxtApp;
  const { loggedIn } = auth;

  // Check loyalty users restriction
  if (contentProtection.onlyLoyaltyUsers) {
    const { isLoyaltyUser } = loyalty;

    if (!isLoyaltyUser.value) {
      const redirectUrl = contentProtection.nonLoyaltyUsersRedirect[0]?.linkUri
        ? $localeRootPath(contentProtection.nonLoyaltyUsersRedirect[0].linkUri)
        : $localeRootPath('/');

      throw createError({
        statusCode: 403,
        statusMessage: 'Access Denied',
        data: { redirectUrl },
      });
    }
  }

  // Check authenticated users restriction
  if (contentProtection.onlyAuthenticatedUsers && !loggedIn.value) {
    throw createError({
      statusCode: 401,
      statusMessage: 'Login Required',
      data: { redirectUrl: $localeRootPath('/account/login') },
    });
  }

  // Check purchased product restriction
  if (contentProtection.hasPurchasedProduct && loggedIn.value) {
    const { userDetails } = userData;
    const hasPurchase = userDetails.value?.blackrollCustomer?.addOns?.some(
      (addOn) => addOn.handle === contentProtection.hasPurchasedProduct
    );

    if (!hasPurchase) {
      throw createError({
        statusCode: 403,
        statusMessage: 'Premium Content',
        data: { redirectUrl: $localeRootPath('/account/exercises/premium') },
      });
    }
  }
};
